import { Configuration, OrderPredictionsApi, SkusApi, StoresApi, SuppliersApi } from '@/api';
import { useSettings } from '@/contexts/SettingsContext';
import { useAuth } from '@/modules/auth/contexts/AuthContext';
import { Sku, SkuAssociation } from '@/modules/store/models/Sku';
import { Store } from '@/modules/store/models/Store';
import { Supplier } from '@/modules/store/models/Supplier';
import AutoOrderService from '@/modules/store/services/AutoOrderService';
import { useQuery } from '@tanstack/react-query';

interface CartQueryResult {
  isLoading: boolean;
  skus: Sku[];
  suppliers: Supplier[];
  store?: Store | null;
  isOrderPredictionAvailable: boolean;
}

const initialData = {
  skus: [],
  suppliers: [],
  store: null,
  isOrderPredictionAvailable: false,
};

const useCartQuery = (
  addedSkuIds?: string[],
  currentSkuIds?: string[],
  storeId?: string,
): CartQueryResult => {
  const { currencyCode, languageCode } = useSettings();
  const skusApi = new SkusApi(new Configuration(useAuth), { currencyCode, languageCode });
  const suppliersApi = new SuppliersApi(new Configuration(useAuth));
  const storesApi = new StoresApi(new Configuration(useAuth));
  const orderPredictionsApi = new OrderPredictionsApi(new Configuration(useAuth));
  const autoOrderService = new AutoOrderService(orderPredictionsApi);

  const { isLoading, data = initialData } = useQuery({
    queryKey: ['cart', addedSkuIds, storeId, currencyCode, languageCode],
    queryFn: async () => {
      if (addedSkuIds === undefined || currentSkuIds === undefined) {
        return initialData;
      }

      const skuPromise = skusApi.getSkus(currentSkuIds, [
        SkuAssociation.PRODUCT,
        SkuAssociation.PRICES,
        SkuAssociation.PROMOTIONS,
      ]);

      const suppliersPromise = suppliersApi.listSuppliers();

      const storePromise: Promise<Store | null | undefined> = storeId
        ? storesApi.getStore(storeId)
        : Promise.resolve(null);

      const orderPredictionPromise = autoOrderService.isPredictionAvailable();

      const [skus, suppliersResponse, store, isOrderPredictionAvailable] = await Promise.all([
        skuPromise,
        suppliersPromise,
        storePromise,
        orderPredictionPromise,
      ]);

      const suppliers = suppliersResponse.items;

      return {
        skus,
        suppliers,
        store,
        isOrderPredictionAvailable,
      };
    },
  });

  return {
    isLoading,
    ...data,
  };
};

export default useCartQuery;
